import React, { memo, useEffect } from "react";
import jsPDF from "jspdf";
import moment from "moment";
import autoTable from "jspdf-autotable";
import { addPageNumber } from "../common/addPageNumber";

interface ScopeEmployeeTimeSheetPdfProps {
  data: any[];
  setPdfBlob: (blob: Blob) => void;
}

const ScopeEmployeeTimeSheetPdf: React.FC<ScopeEmployeeTimeSheetPdfProps> = ({
  data,
  setPdfBlob,
}) => {
  const fmt2 = (val: any) => Number(val || 0).toFixed(2);

  const isMissing = (v: any) => v === null || v === undefined || v === "" || v === "0";

  // Round hours to nearest quarter hour
  const roundToQuarterHour = (hours: number): number => {
    if (hours === 0) return 0;
    const quarters = Math.round(hours * 4) / 4;
    // Handle floating point precision issues
    return Math.round(quarters * 100) / 100;
  };

  const formatTime12Hour = (timeStr: string) => {
    if (!timeStr || timeStr === "0") return "";
    
    const [hours, minutes] = timeStr.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes)) return timeStr;
    
    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12; // Convert 0 to 12 for 12 AM
    
    return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
  };

  const calculateTotalHours = (timeIn: string, timeOut: string) => {
    if (!timeIn || !timeOut || timeIn === "0" || timeOut === "0") return 0;

    const [inHour, inMinute, inSecond = 0] = String(timeIn).split(":").map(Number);
    const [outHour, outMinute, outSecond = 0] = String(timeOut).split(":").map(Number);

    const base = new Date();
    const inTime = new Date(base);
    inTime.setHours(inHour, inMinute, inSecond, 0);

    const outTime = new Date(base);
    outTime.setHours(outHour, outMinute, outSecond, 0);

    const diff = outTime.getTime() - inTime.getTime();
    if (diff <= 0) return 0;
    const hours = diff / (1000 * 60 * 60);
    return roundToQuarterHour(hours);
  };

  // ✅ Excel behavior: if TimeOut missing but TimeIn exists => use SiteTimeOut
  const normalizeTimes = (emp: any) => {
    const noTimes =
      isMissing(emp?.TimeIn) &&
      isMissing(emp?.TimeOut) &&
      isMissing(emp?.AdditionalStart) &&
      isMissing(emp?.AdditionalStop);

    // Regular
    let timeIn = noTimes ? "0" : emp?.TimeIn;
    let timeOut = noTimes ? "0" : emp?.TimeOut;

    if (!isMissing(timeIn) && isMissing(timeOut) && !isMissing(emp?.SiteTimeOut)) {
      timeOut = emp.SiteTimeOut;
    }
    if (!isMissing(timeOut) && isMissing(timeIn) && !isMissing(emp?.SiteTimeIn)) {
      timeIn = emp.SiteTimeIn;
    }

    // Additional
    let additionalStart = noTimes ? "0" : emp?.AdditionalStart;
    let additionalStop = noTimes ? "0" : emp?.AdditionalStop;

    if (!isMissing(additionalStart) && isMissing(additionalStop) && !isMissing(emp?.SiteTimeOut)) {
      additionalStop = emp.SiteTimeOut;
    }
    if (!isMissing(additionalStop) && isMissing(additionalStart) && !isMissing(emp?.SiteTimeIn)) {
      additionalStart = emp.SiteTimeIn;
    }

    return { noTimes, timeIn, timeOut, additionalStart, additionalStop };
  };

  const weekKeyFromBucket = (bucket: any) => {
    const b = String(bucket || "").trim().toLowerCase();
    if (b === "lastweek") return "prev";
    if (b === "currentweek") return "curr";
    return "";
  };

  const mapEmployeeDataToDays = (siteEmployees: any[]) => {
    const sortedEmployees = [...(siteEmployees || [])].sort(
      (a, b) => moment(a.TimeSheetDate).valueOf() - moment(b.TimeSheetDate).valueOf()
    );

    const dayMap = new Map<string, { regularHours: any[]; additionalHours: any[] }>();

    sortedEmployees.forEach((emp) => {
      if (!emp?.TimeSheetDate) return;

      const empDate = moment(emp.TimeSheetDate);
      const dayOfWeek = empDate.format("ddd"); // Mon/Tue/...

      const wk = weekKeyFromBucket(emp.WeekBucket);
      if (!wk) return;

      const key = `${dayOfWeek}-${wk}`;
      if (!dayMap.has(key)) dayMap.set(key, { regularHours: [], additionalHours: [] });

      const { timeIn, timeOut, additionalStart, additionalStop } = normalizeTimes(emp);

      const isAdditional = emp.Paycode && String(emp.Paycode).trim() !== "";
      const regHours = calculateTotalHours(timeIn, timeOut);
      const addHours = calculateTotalHours(additionalStart, additionalStop);

      const entry = {
        date: empDate.format("MM/DD/YYYY"),
        timeIn: formatTime12Hour(timeIn),
        timeOut: formatTime12Hour(timeOut),
        additionalStart: formatTime12Hour(additionalStart),
        additionalStop: formatTime12Hour(additionalStop),
        siteName: emp?.SiteName || "",
        position: emp?.Position || "",
        code: String(emp?.DistNumber ?? ""),
        distNum: String(emp?.DistNumber ?? ""),
        paycode: emp?.Paycode,
        // ✅ keep as STRING so jsPDF doesn't trim trailing 0s
        hours: isAdditional ? fmt2(addHours) : fmt2(regHours),
      };

      if (isAdditional) dayMap.get(key)!.additionalHours.push(entry);
      else dayMap.get(key)!.regularHours.push(entry);
    });

    return dayMap;
  };

  const constructHoursWorkedData = (siteEmployees: any[]) => {
    const dayMap = mapEmployeeDataToDays(siteEmployees);
    const baseDays = ["Mon", "Tue", "Wed", "Thu", "Fri"];

    const hoursWorkedData: any[][] = [];
    const additionalHoursData: any[][] = [];

    ["prev", "curr"].forEach((week) => {
      baseDays.forEach((day) => {
        const dayKey = `${day}-${week}`;
        const dayData = dayMap.get(dayKey);

        if (!dayData) {
          hoursWorkedData.push([day, "", "", "", "", ""]);
          additionalHoursData.push(["", "", "", "", "", "", "", ""]);
          return;
        }

        // Regular
        if (dayData.regularHours.length > 0) {
          dayData.regularHours.forEach((entry: any, index: number) => {
            const isAbsent =
              isMissing(entry?.timeIn) &&
              isMissing(entry?.timeOut) &&
              isMissing(entry?.additionalStart) &&
              isMissing(entry?.additionalStop);

            hoursWorkedData.push([
              index === 0 ? day : "",
              isAbsent ? "" : entry.date,
              isAbsent ? "" : entry.timeIn,
              isAbsent ? "" : entry.timeOut,
              isAbsent ? "" : entry.code,
              isAbsent ? "" : (entry.hours === "0.00" ? "" : entry.hours),
            ]);
          });
        } else {
          hoursWorkedData.push([day, "", "", "", "", ""]);
        }

        // Additional
        if (dayData.additionalHours.length > 0) {
          dayData.additionalHours.forEach((entry: any) => {
            const isAbsent =
              isMissing(entry?.timeIn) &&
              isMissing(entry?.timeOut) &&
              isMissing(entry?.additionalStart) &&
              isMissing(entry?.additionalStop);

            additionalHoursData.push([
              entry.date,
              isAbsent ? "Absent" : entry.additionalStart,
              isAbsent ? "Absent" : entry.additionalStop,
              entry.siteName,
              entry.position,
              entry.code,
              entry.distNum,
              entry.hours, // ✅ string like "2.50"
            ]);
          });
        } else {
          additionalHoursData.push(["", "", "", "", "", "", "", ""]);
        }
      });
    });

    return { hoursWorkedData, additionalHoursData };
  };

  const addBottomTable = (
    doc: jsPDF,
    totalHoursWorked: number,
    totalAdditionalHours: number,
    totalLateness: number,
    totalAbsence: number,
    totalLeftEarly: number,
    hasProgramHours: boolean,
    hasAdditionalHours: boolean
  ) => {
    const pageHeight = doc.internal.pageSize.height;
    const margin = 10;
    let y = pageHeight - 60;

    autoTable(doc, {
      startY: y - 10,
      body: [
        ["For documenting time not at work indicate", ""],
        ["Sick (Personal Illness or Immediate Family)", "Jury Duty"],
        ["Personal Business", "Bereavement (Indicate Relationship)"],
        ["School Closed (as Indicated on Calendar)", "Emergency Closing\n(Snow Days, other authorized emergencies)"],
      ],
      theme: "grid",
      styles: { fontSize: 6, cellPadding: 1, lineColor: 40, lineWidth: 0.1 },
      columnStyles: { 0: { cellWidth: 70 }, 1: { cellWidth: 70 } },
      didParseCell: (data: any) => {
        if (data.row.index === 0 && data.column.index === 0) {
          data.cell.styles.colSpan = 2;
          data.cell.styles.halign = "center";
        }
        if (data.row.index === 0 && data.column.index === 1) {
          data.cell.text[0] = "";
          data.cell.styles.cellWidth = 0;
        }
      },
    });

    autoTable(doc, {
      startY: y - 10,
      body: [
        ["Code", "Explanation / Location", "Code", "Explanation / Location"],
        ["1", "Assigned Role of Acting Director", "3", "Group Leader assigned to role of Small Group Leader"],
        ["2", "Assigned to another program site Permanent sub assigned to a program outside of cluster", "4", "Group Leader assigned to role of Head of Group"],
      ],
      theme: "grid",
      styles: { fontSize: 6.5, cellPadding: 1, lineColor: 40, lineWidth: 0.1 },
      columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 50 }, 2: { cellWidth: 10 }, 3: { cellWidth: 50 } },
      margin: { left: 163 },
    });

    doc.setFontSize(10);
    if (hasProgramHours && !hasAdditionalHours) {
      doc.text("Total:", 256, y - 15);
      doc.text(`${fmt2(totalHoursWorked)} hours`, 266, y - 15);
    }
    if (hasAdditionalHours && !hasProgramHours) {
      doc.text("Total:", 256, y - 15);
      doc.text(`${fmt2(totalAdditionalHours)} hours`, 266, y - 15);
    }
    if (hasProgramHours && hasAdditionalHours) {
      doc.text("Total:", 100, y - 15);
      doc.text(`${fmt2(totalHoursWorked)} hours`, 112, y - 15);
      doc.text("Total:", 256, y - 15);
      doc.text(`${fmt2(totalAdditionalHours)} hours`, 266, y - 15);
    }

    y += 50;

    const drawLine = (x1: number, y1: number, x2: number, y2: number) => {
      doc.setLineWidth(0.3);
      doc.line(x1, y1, x2, y2);
    };

    doc.setFontSize(6);
    doc.text("Total Absences:", 10, y - 25);
    doc.setFontSize(9);
    doc.text(`${totalAbsence}`, margin + 30, y - 25);
    drawLine(margin + 18, y - 24, margin + 45, y - 24);

    doc.setFontSize(6);
    doc.text("Total Lateness:", 70, y - 25);
    doc.setFontSize(9);
    doc.text(`${totalLateness}`, margin + 85, y - 25);
    drawLine(margin + 75, y - 24, margin + 100, y - 24);

    doc.setFontSize(6);
    doc.text("Total Left Early:", 120, y - 25);
    doc.setFontSize(9);
    doc.text(`${totalLeftEarly}`, margin + 138, y - 25);
    drawLine(margin + 125, y - 24, margin + 150, y - 24);
  };

  const generatePDF = (groupedByPersonID: any) => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

    const personIDs = Object.keys(groupedByPersonID);

    personIDs.forEach((personID, personIndex) => {
      const siteData = groupedByPersonID[personID];
      const siteIDs = Object.keys(siteData || {});

      siteIDs.forEach((siteID, siteIndex) => {
        const siteEmployees = siteData[siteID] || [];
        if (!siteEmployees.length) return;

        const siteEmployee = siteEmployees[0];

        const totalHoursWorked = siteEmployees.reduce((sum: number, emp: any) => {
          const { timeIn, timeOut } = normalizeTimes(emp);
          const isAdditional = emp.Paycode && String(emp.Paycode).trim() !== "";
          if (isAdditional) return sum;
          return sum + calculateTotalHours(timeIn, timeOut);
        }, 0);

        const totalAdditionalHours = siteEmployees.reduce((sum: number, emp: any) => {
          const { additionalStart, additionalStop } = normalizeTimes(emp);
          const isAdditional = emp.Paycode && String(emp.Paycode).trim() !== "";
          if (!isAdditional) return sum;
          return sum + calculateTotalHours(additionalStart, additionalStop);
        }, 0);

        let totalLateness = 0;
        let totalAbsence = 0;
        let totalLeftEarly = 0;

        siteEmployees.forEach((entry: any) => {
          const { timeIn, timeOut, additionalStart, additionalStop } = normalizeTimes(entry);
          const dateStr = String(entry?.TimeSheetDate || "").substring(0, 10);

          const timeInDate = !isMissing(timeIn) ? new Date(`${dateStr}T${timeIn}`) : null;
          const timeOutDate = !isMissing(timeOut) ? new Date(`${dateStr}T${timeOut}`) : null;

          const additionalStartDate = !isMissing(additionalStart) ? new Date(`${dateStr}T${additionalStart}`) : null;
          const additionalStopDate = !isMissing(additionalStop) ? new Date(`${dateStr}T${additionalStop}`) : null;

          const siteTimeInDate = !isMissing(entry?.SiteTimeIn) ? new Date(`${dateStr}T${entry.SiteTimeIn}`) : null;
          const siteTimeOutDate = !isMissing(entry?.SiteTimeOut) ? new Date(`${dateStr}T${entry.SiteTimeOut}`) : null;

          if (!timeInDate && !timeOutDate && !additionalStartDate && !additionalStopDate) {
            totalAbsence++;
            return;
          }

          if (
            siteTimeInDate &&
            ((timeInDate && timeInDate > siteTimeInDate) ||
              (additionalStartDate && additionalStartDate > siteTimeInDate))
          ) {
            totalLateness++;
          }

          if (
            siteTimeOutDate &&
            ((timeOutDate && timeOutDate < siteTimeOutDate) ||
              (additionalStopDate && additionalStopDate < siteTimeOutDate))
          ) {
            totalLeftEarly++;
          }
        });

        const pageWidth = doc.internal.pageSize.width + 40;
        const margin = 10;
        let y = margin;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(18);
        doc.text("SCOPE Employee - Time Sheet", margin, y);
        y += 9;

        doc.setFontSize(12);
        doc.text("Name:", margin, y);
        doc.text(`${siteEmployee.LASTNAME}, ${siteEmployee.FIRSTNAME}`, margin + 20, y);
        doc.text("Position:", pageWidth / 2, y);
        doc.text(`${siteEmployee.Position}`, pageWidth / 2 + 30, y);
        y += 7;

        doc.text("District:", margin, y);
        doc.text(`${siteEmployee.DIST_NAM}`, margin + 20, y);
        doc.text("Budget Code:", pageWidth / 2, y);
        doc.text(String(siteEmployee.DistNumber) || "", pageWidth / 2 + 30, y);
        y += 7;

        doc.text("Max Hours:", margin, y);
        doc.text(`${String(siteEmployee.Max_Hours ? siteEmployee.Max_Hours : 0)} a week`, margin + 22, y);
        doc.text("Program:", pageWidth / 2, y);
        doc.text(`${siteEmployee.SITE_NAM}`, pageWidth / 2 + 30, y);
        y += 7;

        doc.text("Schedule:", margin, y);
        const uniqueSchedules = Array.from(new Set(siteEmployees.map((emp: any) => emp?.Schedule).filter((s: any) => s && String(s).toLowerCase() !== "null"))).join(', ');
        doc.text(`${uniqueSchedules}`, margin + 20, y);
        y += 7;
        doc.text("Comment:", margin, y);
        y += 8;

        const { hoursWorkedData, additionalHoursData } = constructHoursWorkedData(siteEmployees);

        const hasProgramHours = hoursWorkedData.some((row) =>
          row.slice(1).some((cell: any) => String(cell ?? "").trim() !== "")
        );
        const hasAdditionalHours = additionalHoursData.some((row) =>
          row.some((cell: any) => String(cell ?? "").trim() !== "")
        );

        doc.setFont("helvetica", "bold");
        if (hasProgramHours && !hasAdditionalHours) doc.text("Hours Worked", margin + 110, y + 8);
        if (hasAdditionalHours && !hasProgramHours) doc.text("Additional Hours / Extra Pay", margin + 110, y + 8);
        if (hasProgramHours && hasAdditionalHours) {
          doc.text("Hours Worked", margin + 50, y + 8);
          doc.text("Additional Hours / Extra Pay", margin + 170, y + 8);
        }

        doc.setFontSize(10);
        if (hasProgramHours && !hasAdditionalHours) doc.text("(Record Actual time worked)", margin + 100, y + 12);
        if (hasAdditionalHours && !hasProgramHours) doc.text("(Record Actual time worked)", margin + 115, y + 12);
        if (hasProgramHours && hasAdditionalHours) {
          doc.text("(Record Actual time worked)", margin + 40, y + 12);
          doc.text("(Record Actual time worked)", margin + 175, y + 12);
        }

        doc.setFont("helvetica", "normal");
        doc.setFontSize(12);

        autoTable(doc, {
          startY: y + 14,
          head: [[
            { content: "", styles: { fillColor: false } },
            { content: "Date", styles: { fillColor: false } },
            { content: "In", styles: { fillColor: false } },
            { content: "Out", styles: { fillColor: false } },
            { content: "Code" },
            { content: "#Hours", styles: { fillColor: false } },
          ]],
          body: hoursWorkedData,
          theme: "grid",
          margin: { right: 180 },
          styles: { fontSize: 8, cellPadding: 1, lineColor: 40, lineWidth: 0.1 },
          headStyles: { fontStyle: "normal", textColor: 0, fillColor: false },
          columnStyles: {
            0: { cellWidth: 40 },
            1: { cellWidth: 50 },
            2: { cellWidth: 50 },
            3: { cellWidth: 50 },
            4: { cellWidth: 40 },
            5: { cellWidth: 40 },
          },
        });

        addBottomTable(
          doc,
          totalHoursWorked,
          totalAdditionalHours,
          totalLateness,
          totalAbsence,
          totalLeftEarly,
          hasProgramHours,
          hasAdditionalHours
        );

        const isLastSiteForPerson = siteIndex === siteIDs.length - 1;
        const isLastPerson = personIndex === personIDs.length - 1;
        if (!(isLastPerson && isLastSiteForPerson)) doc.addPage();
      });
    });

    addPageNumber(doc);
    setPdfBlob(doc.output("blob"));
  };

  useEffect(() => {
    if (!data || !data.length) return;

    const groupedByPersonID = data.reduce(
      (acc: { [personID: string]: { [siteID: string]: any[] } }, item: any) => {
        const personID = item.PersonID;
        const siteID = item.SiteID;

        if (!acc[personID]) acc[personID] = {};
        if (!acc[personID][siteID]) acc[personID][siteID] = [];

        acc[personID][siteID].push(item);
        return acc;
      },
      {}
    );

    generatePDF(groupedByPersonID);
  }, [data]);

  return null;
};

export default memo(ScopeEmployeeTimeSheetPdf);
